import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Restaurant.css";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import { BranchesDetails } from "./branchesDetails/BranchesDetails";
import { UserInfoDetails } from "./userInfo/UserInfoDetails";
import { DeleteModal } from "../modals/Delete";
import { translations } from "../../utils/lang";
import { deleteStoreAPI, updateStoreAPI } from "../../utils/api/stores";
import { CloudDownload, FileDown, Trash2 } from "../ui/icons";
import Button from "../ui/Button";
import StatusDropdown from "../ui/StatusDropdown";
import GroupDetails from "./details/GroupDetails";
import {
  exportJoiningRequirements,
  validateJoiningExport,
} from "../../utils/exportJoiningRequirements";
import {
  exportConnectionInfo,
  validateConnectionExport,
} from "../../utils/exportConnectionInfo";

const restStatus = [
  "new",
  "pending",
  "active",
  "closed",
  "inProgress",
  "startSubscription",
];

// Any of these allows a change to the branches list (finer checks run in the child handlers).
const BRANCH_CHANGE_PERMISSIONS = [
  "restaurant.branch.create",
  "restaurant.branch.update",
  "restaurant.branch.delete",
  "restaurant.branch.status.update",
  "restaurant.branch.workTime.update",
  "restaurant.branch.workTime.delete",
  "restaurant.branch.appUser.create",
  "restaurant.branch.appUser.update",
  "restaurant.branch.appUser.statusUpdate",
];

export const RestaurantRow = ({ item, fetchStores }) => {
  const {
    lang,
    handleTokenExpiration,
  } = useContext(Context);
  const { can, canAny } = useAccess();
  const resource = { restaurantId: item.id };

  const [expandedField, setExpandedField] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const navigate = useNavigate();

  const isDetailsFull = useMemo(() => {
    return (
      !Object.values(item.details || {}).some((v) => !v) &&
      [
        "commercialRegister",
        "taxCertificate",
        "menuPdf",
        "restaurantLogo",
      ].every((k) => item.moreDetails?.[k])
    );
  }, [item.details, item.moreDetails]);

  const isDetails2Full = useMemo(() => {
    return !Object.values(item.moreDetails || {}).some((v) => !v);
  }, [item.moreDetails]);

  const isAllActive = useMemo(() => {
    return (item.userInfo || []).every(
      (user) => (user.status || "active") !== "inactive",
    );
  }, [item.userInfo]);

  const isBranchesActive = useMemo(() => {
    if (!item.branches) return false;

    if (item.branches.length !== item.branchNumber) return false;

    return item.branches.every((branch) => {
      const requiredFieldsFilled =
        branch.city &&
        branch.location &&
        branch.coordination &&
        branch.name &&
        branch.phone;

      if (!requiredFieldsFilled) return false;

      const appsActive = (branch.apps || []).every(
        (app) => (app.status || "active") !== "inactive",
      );

      return appsActive;
    });
  }, [item.branches, item.branchNumber]);

  const toggleExpand = (field) => {
    setExpandedField((prev) => (prev === field ? null : field));
  };

  const confirmDelete = (id) => {
    setDeleteTarget(id);
  };

  const handleStatusChange = async (newStatus) => {
    if (!can("restaurant.status.update", resource)) return;
    const token = localStorage.getItem("auth-token");
    if (token) {
      const updatedStore = await updateStoreAPI(token, item._id, {
        status: newStatus,
      });
      if (updatedStore) {
        fetchStores();
      } else {
        handleTokenExpiration();
      }
    } else {
      handleTokenExpiration();
    }
  };

  const [exportModal, setExportModal] = useState({
    open: false,
    text: "",
  });

  const handleConfirmedDelete = async (id) => {
    if (!can("restaurant.delete", resource)) return;
    const token = localStorage.getItem("auth-token");
    if (token) {
      const deletedStore = await deleteStoreAPI(token, id);
      if (deletedStore) {
        fetchStores();
        setDeleteTarget(null);
      } else {
        handleTokenExpiration();
      }
    } else {
      handleTokenExpiration();
    }
  };

  return (
    <>
      <tr>
        <td className="restaurants-list-table-p">
          <p>{new Date(item.date).toLocaleDateString()}</p>
        </td>
        <td className="restaurants-list-table-p">
          <p>{item.id}</p>
        </td>
        {can("restaurant.details.open") ? (
          <td
            className={`restaurants-list-table-p ${!isDetailsFull ? "clickable-yellow" : "clickable"} ${isDetails2Full ? "" : "notFull"}`}
            onClick={() => toggleExpand("store")}
          >
            <div className="rest-name-row clickable-p">
              <p>{item.businessName}</p>
            </div>
          </td>
        ) : (
          <td className="restaurants-list-table-p">
            <p>{item.businessName}</p>
          </td>
        )}
        <td
          className={`restaurants-list-table-p ${!isBranchesActive ? "clickable-yellow" : "clickable"}`}
          onClick={() => toggleExpand("branches")}
        >
          <div className="rest-name-row">
            <p>
              {item.branchNumber}/{item.branches?.length}
            </p>
          </div>
        </td>

        <td className="restaurants-list-table-p">
          <p>{item.name}</p>
        </td>
        <td
          className={`restaurants-list-table-p ${!isAllActive ? "clickable-yellow" : "clickable"}`}
          onClick={() => toggleExpand("userInfo")}
        >
          <div className="rest-name-row clickable-p">
            {can("restaurant.apps.view") &&
              item.userInfo.map((u, appIndex) => {
                if (u.status === "active")
                  return (
                    <div
                      key={appIndex}
                      className={`active-apps-to-branch ${u.app}`}
                      title={u.app}
                    ></div>
                  );
                return null;
              })}
            <p>{item.phone}</p>
          </div>
        </td>
        <td className="restaurants-list-table-p">
          <p>{item.city}</p>
        </td>
        <td className="restaurants-list-table-p">
          <div className="restaurant-status-cell">
            <StatusDropdown
              value={item.status}
              options={restStatus}
              editable={can("restaurant.status.update", resource)}
              onChange={handleStatusChange}
              getLabel={(status) =>
                status === "startSubscription"
                  ? lang === "ar"
                    ? "بدء الاشتراك"
                    : "Start Subscription"
                  : translations[lang].restaurants.restaurantsRow.status[status]
              }
            />
          </div>
        </td>
        {item.status === "startSubscription" &&
          can("restaurant.onboarding.continue", resource) && (
            <td className="restaurants-list-table-p">
              <Button
                className="restaurant-onboarding-btn"
                type="button"
                variant="primary"
                appearance="soft"
                size="sm"
                onClick={() =>
                  navigate("/auth/store-onboarding?onboardingMode=store")
                }
              >
                {lang === "ar" ? "متابعة التهيئة" : "Continue Onboarding"}
              </Button>
            </td>
          )}
        {can("restaurant.joiningRequirements.export", resource) && (
          <td className="restaurants-list-table-p">
            <div className="restaurant-actions">
              {can("restaurant.delete", resource) && (
                <Button
                  className="restaurants-table-btn remove"
                  onClick={() => confirmDelete(item._id)}
                  title={lang === "ar" ? "حذف" : "Delete"}
                  variant="danger"
                  appearance=""
                  size="sm"
                  rounded
                  icon={<Trash2 size={18} />}
                />
              )}

              <Button
                className="restaurants-table-btn export"
                title={
                  lang === "ar"
                    ? "تصدير متطلبات الانضمام"
                    : "Export Joining Requirements"
                }
                variant="primary"
                appearance="soft"
                size="sm"
                rounded
                icon={<FileDown size={18} />}
                onClick={async () => {
                  // Checked in the handler too: a denied export builds and downloads no file (NA-010).
                  if (!can("restaurant.joiningRequirements.export", resource)) return;
                  const validation = validateJoiningExport(item);

                  if (!validation.valid) {
                    setExportModal({
                      open: true,
                      text: validation.message,
                    });

                    return;
                  }

                  await exportJoiningRequirements(item);
                }}
              />
              <Button
                className="restaurants-table-btn export"
                title={
                  lang === "ar"
                    ? "تصدير معلومات الربط"
                    : "Export Connection Info"
                }
                variant="primary"
                appearance="soft"
                size="sm"
                rounded
                icon={<CloudDownload size={18} />}
                onClick={async () => {
                  if (!can("restaurant.joiningRequirements.export", resource)) return;
                  const validation = validateConnectionExport(item);

                  if (!validation.valid) {
                    setExportModal({
                      open: true,
                      text: validation.message,
                    });

                    return;
                  }

                  await exportConnectionInfo(item);
                }}
              />
            </div>
          </td>
        )}
      </tr>

      {expandedField && (
        <tr className="expanded-row">
          <td colSpan="10">
            <div className="expanded-box">
              {expandedField === "store" && (
                <GroupDetails
                  restaurantId={item.id}
                  data={item.details}
                  onSave={async (updated) => {
                    if (!can("restaurant.details.update", resource)) return;
                    const token = localStorage.getItem("auth-token");
                    if (token) {
                      const updatedStore = await updateStoreAPI(
                        token,
                        item._id,
                        { details: updated },
                      );
                      if (updatedStore) {
                        fetchStores();
                      } else {
                        handleTokenExpiration();
                      }
                    } else {
                      handleTokenExpiration();
                    }
                  }}
                  moreData={item?.moreDetails}
                  onSaveMoreData={async (updated) => {
                    if (!can("restaurant.details.update", resource)) return;
                    const token = localStorage.getItem("auth-token");
                    if (token) {
                      const updatedStore = await updateStoreAPI(
                        token,
                        item._id,
                        {
                          moreDetails: {
                            ...updated,
                            salesPhone: updated.salesPhone || "0547179368",
                            salesEmail:
                              updated.salesEmail || "restaurants@lud.app",
                          },
                        },
                      );
                      if (updatedStore) {
                        fetchStores();
                      } else {
                        handleTokenExpiration();
                      }
                    } else {
                      handleTokenExpiration();
                    }
                  }}
                  mainData={item}
                  onSaveMain={async (updated) => {
                    if (!can("restaurant.details.update", resource)) return;
                    const token = localStorage.getItem("auth-token");
                    if (token) {
                      const updatedStore = await updateStoreAPI(
                        token,
                        item._id,
                        updated,
                      );
                      if (updatedStore) {
                        fetchStores();
                      } else {
                        handleTokenExpiration();
                      }
                    } else {
                      handleTokenExpiration();
                    }
                  }}
                />
              )}
              {expandedField === "branches" && (
                <BranchesDetails
                  restaurantId={item.id}
                  branches={item.branches}
                  onUpdate={async (updatedBranches) => {
                    if (!canAny(BRANCH_CHANGE_PERMISSIONS, resource)) return;
                    const token = localStorage.getItem("auth-token");
                    if (token) {
                      const updatedStore = updateStoreAPI(token, item._id, {
                        branches: updatedBranches,
                      });
                      if (updatedStore) {
                        fetchStores();
                      } else {
                        handleTokenExpiration();
                      }
                    } else {
                      handleTokenExpiration();
                    }
                  }}
                  branchesLimit={item.branchNumber}
                />
              )}
              {expandedField === "userInfo" && (
                <UserInfoDetails
                  restaurantId={item.id}
                  userInfo={item.userInfo || []}
                  appInfo={item.appInfo || []}
                  onUpdate={async (updatedUsers) => {
                    if (!canAny(["restaurant.userInfo.update", "restaurant.app.manage"], resource)) return;
                    const token = localStorage.getItem("auth-token");
                    if (token) {
                      const updateStore = await updateStoreAPI(
                        token,
                        item._id,
                        {
                          userInfo: updatedUsers.userInfo,
                          appInfo: updatedUsers.appInfo,
                        },
                      );
                      if (updateStore) {
                        fetchStores();
                      } else {
                        handleTokenExpiration();
                      }
                    } else {
                      handleTokenExpiration();
                    }
                  }}
                />
              )}
            </div>
          </td>
        </tr>
      )}
      {deleteTarget && (
        <DeleteModal
          id={deleteTarget}
          handleDelete={handleConfirmedDelete}
          cancel={() => setDeleteTarget(null)}
        />
      )}
      {exportModal.open && (
        <div className="export-validation-modal">
          <div className="export-validation-content">
            <div className="export-validation-icon">!</div>

            <h3>
              {lang === "ar"
                ? "لا يمكن تحميل الملف"
                : "File cannot be downloaded"}
            </h3>

            <p>{exportModal.text}</p>

            <Button
              className="export-validation-btn"
              onClick={() =>
                setExportModal({
                  open: false,
                  text: "",
                })
              }
              variant="primary"
              appearance="filled"
              fullWidth
            >
              {lang === "ar" ? "إغلاق" : "Close"}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
